/**
 * The vocabulary the image uploader's three sides have to share: the cap, and
 * the shape of a row on screen.
 *
 * In `lib/` because it is named from opposite sides of the dependency arrow —
 * the L6 field draws these rows, the L5 hook holds them, and an L4 contract
 * test pins the cap against Python and against ADR-0003. `lib/` is the tier
 * below both graphs (`client/docs/architecture.md`, "The dependency rule"), so
 * the shared word can sit under all three without anyone importing upward. It
 * is not a browser-seam schema, so it does not belong in `api/contracts/`.
 */

/**
 * How many images an item takes.
 *
 * **Six, not the donor's three.** The donor's uploader is `limit={3}`; ADR-0003
 * ("Upload path and caps") raises the affordance to six as an *approved parity
 * divergence*, on the grounds that six matches current business need and every
 * alive migrated item already fits. That is an owner ruling on the record,
 * which is the only thing that licenses a divergence — so the number is pinned
 * against the ADR's own text, and against Python's `MAX_ITEM_IMAGES`, in
 * `catalog-item-rules-drift.unit.test.ts`. Pinning the two code sides to each
 * other alone would let them agree on a number the owner never approved.
 *
 * The default rather than the rule: the field still takes a `limit`.
 */
export const DEFAULT_IMAGE_LIMIT = 6;

export interface UploadedImage {
  /**
   * What this row *is*, for as long as it is on screen — assigned once, when
   * the row appears, and never rewritten.
   *
   * `id` cannot do this job. A picked file's row draws before its bytes are
   * up, under a placeholder id, and the upload rewrites that id in place when
   * the PUT lands (`item-images-uploader.ts`) — so anything holding a row by
   * its id loses the row mid-life, for no reason a user could see. Nor can a
   * position: reordering moves every row past the one that moved. The drag
   * needs a name that survives both, and so does React's reconciliation.
   */
  key: string;
  /**
   * The catalog image row's id — what a save attaches.
   *
   * A **placeholder** while `pending`, because the donor draws the picked file
   * before its bytes are up and Atlas has no id until they are — which is why
   * it is not this row's identity. `key` is.
   */
  id: number;
  /** What to draw. A local object URL while the bytes are in flight. */
  previewUrl: string;
  /** As the donor names a row, and how a failure finds its message again. */
  fileName: string;
  /** The bytes are still going up. The row draws, but it cannot be saved yet. */
  pending?: boolean;
  /**
   * The upload failed. The row stays — the message under it tells the typist to
   * remove it — but there is no image behind it to save.
   */
  failed?: boolean;
}

/**
 * What a host says when a file's bytes did not land.
 *
 * The donor has two sentences for one event — "Error uploading image '{name}'.
 * Remove image and then try uploading again." in the Request Item dialog's
 * `ItemsError`, and "Image upload failed. Please try again." in the item edit
 * form's own `Snackbar` (`AssetEditForm.tsx:429`). The second describes a flow
 * Atlas does not have: the donor's edit form *removes* the failed row, where the
 * behaviour Atlas shipped in #344 keeps it and holds submit until it is taken
 * away — recorded in #300's rescope NOTE as delivered, though never ruled on as
 * a parity break in its own right (#252's ruled breaks are D3 and D7; its D1 is
 * the packaging ruling), so it is named for the owner on #300. Under that ruling the dialog's sentence is the true one on
 * both screens, because on both screens removal is the gesture that unblocks the
 * form — so it is said once here rather than twice at the call sites.
 */
export const imageUploadFailureMessage = (fileName: string): string =>
  `Error uploading image '${fileName}'. Remove image and then try uploading again.`;
